/**
 * GodModePanel — Persistent side panel for deep entity inspection.
 *
 * Shows inspectEntity() output grouped by category.
 * Supports left/right positioning. Closes on ESC or button click.
 */

import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useGodMode } from '../context/GodModeContext';
import { inspectEntity } from '../utils/godModeInspect';
import { activateOnEnterOrSpace } from '../utils/a11y';

const CATEGORY_CONFIG = {
  identity:   { label: 'Identity',   icon: '🆔', color: '#AFA9EC' },
  status:     { label: 'Status',     icon: '📊', color: '#5DCAA5' },
  metrics:    { label: 'Metrics',    icon: '📈', color: '#F5A623' },
  timestamps: { label: 'Timestamps', icon: '🕐', color: '#85B7EB' },
  config:     { label: 'Config',     icon: '⚙️', color: '#ED93B1' },
  raw:        { label: 'Raw Data',   icon: '📦', color: '#999' },
};

const TYPE_ICONS = {
  annotator: '👤',
  job: '📋',
  application: '📩',
  assignment: '🔗',
};

export default function GodModePanel() {
  const { selectedEntity, panelSide, closePanel } = useGodMode();
  const [copiedField, setCopiedField] = useState(null);
  const location = useLocation();

  // Close on Escape
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') closePanel();
  }, [closePanel]);

  useEffect(() => {
    if (selectedEntity) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [selectedEntity, handleKeyDown]);

  // Close panel on navigation
  useEffect(() => {
    closePanel();
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!selectedEntity) return null;

  const { type, entity } = selectedEntity;
  const fields = inspectEntity(type, entity);
  const name = entity.name || entity.title || entity.stage || 'Record';

  // Group by category
  const grouped = {};
  fields.forEach(f => {
    if (!grouped[f.category]) grouped[f.category] = [];
    grouped[f.category].push(f);
  });

  const isLeft = panelSide === 'left';

  async function copyToClipboard(text, fieldLabel) {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldLabel);
      setTimeout(() => setCopiedField(null), 1500);
    } catch { /* intentionally empty */ }
  }

  return (
    <div className="god-mode-panel" style={{
      position: 'fixed',
      [isLeft ? 'left' : 'right']: isLeft ? 72 : 0,
      top: 0, bottom: 0,
      width: 360,
      zIndex: 250,
      background: '#0d0d1a',
      borderLeft: isLeft ? 'none' : '1px solid rgba(175,169,236,0.2)',
      borderRight: isLeft ? '1px solid rgba(175,169,236,0.2)' : 'none',
      overflowY: 'auto',
      boxShadow: isLeft ? '4px 0 20px rgba(0,0,0,0.3)' : '-4px 0 20px rgba(0,0,0,0.3)',
      fontFamily: "'DM Mono', monospace",
      color: '#e0e0e0',
      animation: 'godPanelSlideIn 0.2s ease-out',
    }}>
      {/* Header */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 1,
        padding: '14px 16px',
        background: '#0d0d1a',
        borderBottom: '1px solid rgba(175,169,236,0.15)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>{TYPE_ICONS[type] || '📦'}</span>
          <div>
            <span style={{
              fontSize: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em',
              padding: '1px 6px', borderRadius: 3, marginRight: 6,
              background: 'rgba(175,169,236,0.15)', color: '#AFA9EC',
            }}>{type}</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{name}</span>
          </div>
        </div>
        <button onClick={closePanel} style={{
          background: 'none', border: '1px solid rgba(175,169,236,0.2)', borderRadius: 4,
          color: '#AFA9EC', cursor: 'pointer', padding: '2px 8px', fontSize: 11,
          fontFamily: "'DM Mono', monospace",
        }}>✕</button>
      </div>

      {/* Body — grouped fields */}
      <div style={{ padding: '8px 0' }}>
        {Object.entries(grouped).map(([category, categoryFields]) => {
          const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.raw;
          return (
            <div key={category} style={{ marginBottom: 4 }}>
              {/* Category header */}
              <div style={{
                padding: '8px 16px 4px',
                fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em',
                color: config.color, display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <span>{config.icon}</span> {config.label}
              </div>

              {/* Fields */}
              {categoryFields.map((field, i) => (
                <div key={`${category}-${i}`} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                  padding: '4px 16px',
                  borderBottom: '1px solid rgba(255,255,255,0.03)',
                }}>
                  <span style={{ fontSize: 10, color: '#666', minWidth: 100, flexShrink: 0 }}>
                    {field.label}
                  </span>
                  {field.copyable ? (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => copyToClipboard(field.fullValue || field.value, field.label)}
                      onKeyDown={activateOnEnterOrSpace(() => copyToClipboard(field.fullValue || field.value, field.label))}
                      title={`Click to copy: ${field.fullValue || field.value}`}
                      style={{
                        fontSize: 11, color: '#AFA9EC', textAlign: 'right',
                        wordBreak: 'break-all', maxWidth: 220,
                        cursor: 'copy',
                        textDecoration: 'underline dotted rgba(175,169,236,0.3)',
                      }}
                    >
                      {copiedField === field.label ? '✓ Copied!' : field.value}
                    </span>
                  ) : (
                    <span
                      style={{
                        fontSize: 11, color: '#ccc', textAlign: 'right',
                        wordBreak: 'break-all', maxWidth: 220,
                        cursor: 'default',
                      }}
                    >
                      {copiedField === field.label ? '✓ Copied!' : field.value}
                    </span>
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div style={{
        padding: '12px 16px', borderTop: '1px solid rgba(175,169,236,0.1)',
        fontSize: 9, color: '#444', textAlign: 'center',
      }}>
        ⚡ God Mode Inspection · ESC to close · Click IDs to copy
      </div>
    </div>
  );
}
